import React from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Box,
  Grid,
  Divider,
  IconButton,
  Icon,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";

const modalStyles = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", md: "800px" },
  maxHeight: "90vh",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: "8px",
  overflowY: "auto",
};

function DetallesOrdenCompra({ open, onClose, orden }) {
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  if (!orden) return null;

  const getEstatusColor = (estatus) => {
    switch (estatus) {
      case "recibida":
        return "success";
      case "pendiente":
        return "warning";
      case "parcial":
        return "info";
      case "cancelada":
        return "error";
      default:
        return "default";
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(value || 0);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyles}>
        {/* Header */}
        <MDBox
          variant="gradient"
          bgColor={sidenavColor}
          borderRadius="8px 8px 0 0"
          p={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <MDBox>
            <MDTypography variant="h5" color="white" fontWeight="medium">
              Detalles de Orden de Compra
            </MDTypography>
            <MDTypography variant="button" color="white" fontWeight="regular">
              Folio: {orden.folio}
            </MDTypography>
          </MDBox>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </MDBox>

        <MDBox p={3}>
          <Grid container spacing={3}>
            {/* Resumen Principal */}
            <Grid item xs={12}>
              <Card sx={{ bgcolor: "#f8f9fa", boxShadow: "none", border: "1px solid #e0e0e0" }}>
                <MDBox p={2} display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
                  <MDBox>
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontWeight="bold"
                      textTransform="uppercase"
                    >
                      Estatus
                    </MDTypography>
                    <MDBox mt={0.5}>
                      <Chip
                        label={orden.estatus}
                        color={getEstatusColor(orden.estatus)}
                        size="small"
                      />
                    </MDBox>
                  </MDBox>
                  <MDBox>
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontWeight="bold"
                      textTransform="uppercase"
                    >
                      Fecha Orden
                    </MDTypography>
                    <MDTypography variant="h6">
                      {new Date(orden.fecha_orden).toLocaleDateString("es-MX")}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontWeight="bold"
                      textTransform="uppercase"
                    >
                      Tipo de Compra
                    </MDTypography>
                    <MDTypography variant="h6" textTransform="capitalize">
                      {orden.tipo_compra || "N/A"}
                    </MDTypography>
                  </MDBox>
                  <MDBox textAlign="right">
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontWeight="bold"
                      textTransform="uppercase"
                    >
                      Total
                    </MDTypography>
                    <MDTypography variant="h5" color={sidenavColor} fontWeight="bold">
                      {formatCurrency(orden.total)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>

            {/* Información del Proveedor y Sucursal */}
            <Grid item xs={12} md={6}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Proveedor</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <MDBox>
                    <MDTypography variant="button" color="text" fontWeight="bold">
                      Nombre:{" "}
                    </MDTypography>
                    <MDTypography variant="button">{orden.proveedor?.nombre || "N/A"}</MDTypography>
                  </MDBox>
                  <MDBox mt={1}>
                    <MDTypography variant="button" color="text" fontWeight="bold">
                      RFC:{" "}
                    </MDTypography>
                    <MDTypography variant="button">{orden.proveedor?.rfc || "N/A"}</MDTypography>
                  </MDBox>
                  {orden.proveedor?.telefono && (
                    <MDBox mt={1}>
                      <MDTypography variant="button" color="text" fontWeight="bold">
                        Teléfono:{" "}
                      </MDTypography>
                      <MDTypography variant="button">{orden.proveedor.telefono}</MDTypography>
                    </MDBox>
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>

            <Grid item xs={12} md={6}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Entrega / Almacén</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <MDBox>
                    <MDTypography variant="button" color="text" fontWeight="bold">
                      Sucursal:{" "}
                    </MDTypography>
                    <MDTypography variant="button">{orden.sucursal?.nombre || "N/A"}</MDTypography>
                  </MDBox>
                  <MDBox mt={1}>
                    <MDTypography variant="button" color="text" fontWeight="bold">
                      Fecha Estimada:{" "}
                    </MDTypography>
                    <MDTypography variant="button">
                      {orden.fecha_entrega_estimada
                        ? new Date(orden.fecha_entrega_estimada).toLocaleDateString("es-MX")
                        : "No definida"}
                    </MDTypography>
                  </MDBox>
                  <MDBox mt={1}>
                    <MDTypography variant="button" color="text" fontWeight="bold">
                      Usuario:{" "}
                    </MDTypography>
                    <MDTypography variant="button">{orden.usuario?.name || "N/A"}</MDTypography>
                  </MDBox>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Detalles de Productos */}
            <Grid item xs={12}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Productos Solicitados</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table size="small">
                      <TableHead sx={{ display: "table-header-group" }}>
                        <TableRow>
                          <TableCell>Producto</TableCell>
                          <TableCell align="center">Cantidad</TableCell>
                          <TableCell align="right">Precio Unit.</TableCell>
                          <TableCell align="center">Desc.</TableCell>
                          <TableCell align="right">Subtotal</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {orden.detalles && orden.detalles.length > 0 ? (
                          orden.detalles.map((detalle, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                {detalle.producto?.nombre || "Producto desconocido"}
                              </TableCell>
                              <TableCell align="center">{detalle.cantidad}</TableCell>
                              <TableCell align="right">
                                {formatCurrency(detalle.precio_unitario)}
                              </TableCell>
                              <TableCell align="center">
                                {formatCurrency(detalle.descuento)}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(detalle.subtotal)}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              No hay productos registrados
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow>
                          <TableCell colSpan={3} />
                          <TableCell align="right">
                            <strong>Subtotal</strong>
                          </TableCell>
                          <TableCell align="right">{formatCurrency(orden.subtotal)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={3} />
                          <TableCell align="right">
                            <strong>Impuestos</strong>
                          </TableCell>
                          <TableCell align="right">{formatCurrency(orden.impuesto)}</TableCell>
                        </TableRow>
                        {orden.descuento > 0 && (
                          <TableRow>
                            <TableCell colSpan={3} />
                            <TableCell align="right">
                              <strong>Descuento Gral.</strong>
                            </TableCell>
                            <TableCell align="right">-{formatCurrency(orden.descuento)}</TableCell>
                          </TableRow>
                        )}
                        <TableRow>
                          <TableCell colSpan={3} />
                          <TableCell align="right">
                            <strong>TOTAL</strong>
                          </TableCell>
                          <TableCell align="right">
                            <MDTypography variant="h6" color={sidenavColor} fontWeight="bold">
                              {formatCurrency(orden.total)}
                            </MDTypography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Notas */}
            {orden.notas && (
              <Grid item xs={12}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Notas / Observaciones</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <MDTypography variant="button" color="text">
                      {orden.notas}
                    </MDTypography>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            )}
          </Grid>
        </MDBox>

        {/* Footer Actions */}
        <MDBox p={3} display="flex" justifyContent="flex-end">
          <MDButton variant="outlined" color="secondary" onClick={onClose}>
            Cerrar
          </MDButton>
        </MDBox>
      </Box>
    </Modal>
  );
}

DetallesOrdenCompra.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  orden: PropTypes.object,
};

export default DetallesOrdenCompra;
